/**
 * Robust "copy to clipboard" helper.
 *
 * The async Clipboard API (`navigator.clipboard.writeText`) is the modern
 * path, but it has two sharp edges that bit us in practice:
 *
 *   1. It requires the document to be *focused*. If an `await` (e.g. a
 *      network fetch to lazy-load a secret) runs between the user's click
 *      and the write, focus can drift and the call rejects with
 *      "Failed to execute 'writeText' on 'Clipboard': Document is not
 *      focused." Callers should therefore copy a value they already hold
 *      rather than fetch-then-copy — but we also defend here.
 *   2. It only works in a secure context (https / localhost).
 *
 * So we try the async API first, and on *any* failure fall back to the
 * legacy hidden-textarea + `document.execCommand("copy")` trick, which
 * operates on a synchronous selection and does not depend on document
 * focus in the same way. If both fail we throw, so the caller can fall
 * back to revealing the value for manual copy.
 */
export async function copyText(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall through to the legacy path (covers "Document is not focused"
      // and other transient activation/focus failures).
    }
  }

  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "-9999px";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  ta.setSelectionRange(0, text.length);

  let ok = false;
  try {
    ok = document.execCommand("copy");
  } finally {
    document.body.removeChild(ta);
  }

  if (!ok) {
    throw new Error("Clipboard copy was blocked by the browser.");
  }
}
